import { Enum, Notification, Pagination } from '../../../interfaces';
import React, { FC, useEffect, useState } from 'react';
import { Empty } from '../../components';
import { ConfigProvider, Pagination as AntPagination, Spin } from 'antd';
import fa_IR from 'antd/locale/fa_IR';
import { getNotificationReasons, getNotifications, readNotification, getUnreadCount } from 'services';
import { FaBell, FaExchangeAlt, FaLock, FaMoneyBillWave } from 'react-icons/fa';
import moment from 'jalali-moment';
import { toast } from 'react-toastify';

const isReadFilters: Enum[] = [
    { id: 0, title: 'همه' },
    { id: 1, title: 'خوانده شده' },
    { id: 2, title: 'خوانده نشده' },
];

interface Props {
    /** If true, renders a compact list (no filters/pagination UI) for popovers */
    isPreview?: boolean;
    /** Optional callback to bubble unread count to parent (e.g., Header badge) */
    onCountChange?: (count: number) => void;
}

export const Notifications: FC<Props> = ({ isPreview, onCountChange }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [notifications, setNotifications] = useState<Pagination<Notification> | undefined>();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [reasons, setReasons] = useState<Enum[]>();

    const [selectedReason, setSelectedReason] = useState<Enum>();
    const [selectedIsReadFilter, setSelectedIsReadFilter] = useState<Enum>();

    const [readNotificationLoading, setReadNotificationLoading] = useState<boolean>(false);

    // separate loading state for unread-count fetch to prevent list spinner flicker
    const [countLoading, setCountLoading] = useState<boolean>(false);

    const renderIcon = (item: Notification) => {
        switch (item.reason.id) {
            case 1:
                return (
                    <div className="flex items-center justify-center w-8 h-8 bg-background rounded-full text-primary-100 max-lg:w-6 max-lg:h-6">
                        <FaExchangeAlt />
                    </div>
                );
            case 2:
                return (
                    <div className="flex items-center justify-center w-8 h-8 bg-background rounded-full text-primary-100 max-lg:w-6 max-lg:h-6">
                        <FaMoneyBillWave />
                    </div>
                );
            case 3:
                return (
                    <div className="flex items-center justify-center w-8 h-8 bg-background rounded-full text-primary-100 max-lg:w-6 max-lg:h-6">
                        <FaBell />
                    </div>
                );
            case 4:
                return (
                    <div className="flex items-center justify-center w-8 h-8 bg-background rounded-full text-primary-100 max-lg:w-6 max-lg:h-6">
                        <FaLock />
                    </div>
                );
            default:
                return <></>;
        }
    };

    const handlePageChange = (p: number, size: number) => {
        setPage(p);
        setPageSize(size);
    };

    const buildQueryString = () => {
        const params = new URLSearchParams();

        if (selectedIsReadFilter?.id !== 0) {
            if (selectedIsReadFilter?.id === 1) params.append('isRead', 'true');
            if (selectedIsReadFilter?.id === 2) params.append('isRead', 'false');
        }
        if (selectedReason) {
            params.append('reason', selectedReason.id.toString());
        }

        return params.toString();
    };

    const refreshUnreadCount = () => {
        // avoid toggling the main list spinner
        getUnreadCount(setCountLoading, (count) => onCountChange?.(count));
    };

    const fetchData = (p: number, size: number) => {
        const queryString = buildQueryString();
        const finalQueryString = queryString ? `?${queryString}` : '';
        getNotifications(setLoading, setNotifications, p, size, finalQueryString);
        // keep the badge in sync regardless of current list filters
        refreshUnreadCount();
    };

    const onRead = (item: Notification) => {
        if (!item.isRead && !readNotificationLoading) {
            const onComplete = () => {
                toast.success('اعلان با موفقیت خوانده شد.');
                fetchData(page, pageSize); // refresh list & count
            };
            readNotification(setReadNotificationLoading, { notificationId: item.id }, onComplete);
        }
    };

    useEffect(() => {
        getNotificationReasons(setLoading, setReasons);
        refreshUnreadCount(); // initial unread count
    }, []);

    useEffect(() => {
        fetchData(page, pageSize);
    }, [page, pageSize, selectedIsReadFilter, selectedReason]);

    return (
        <>
            {!isPreview && (
                <div className="flex max-lg:flex-col gap-4 justify-between items-center mt-6 w/full bg-white h-14 px-4 border border-primary-20 rounded-t-[8px] max-lg:rounded-[8px] max-lg:h-auto max-lg:rounded-b-none">
                    <div className="flex min-w-fit items-center gap-4 text-sm font-medium relative h-full overflow-x-scroll scrollbar-hidden max-lg:gap-4 max-lg:justify-start max-lg:w-full max-lg:border-b max-lg:border-primary-20">
                        {isReadFilters.map((isRead) => (
                            <div
                                key={isRead.id}
                                className="relative clickable h-full flex items-center"
                                onClick={() => {
                                    setSelectedIsReadFilter(selectedIsReadFilter?.id === isRead.id ? undefined : isRead);
                                }}
                            >
                                <span
                                    className={`py-2 px-4 transition duration-300 ease-in-out ${selectedIsReadFilter?.id === isRead.id ? 'text-black-100' : 'text-black-60'
                                        }`}
                                >
                                    {isRead.title}
                                </span>
                                {selectedIsReadFilter?.id === isRead.id && (
                                    <div className="absolute left-0 right-0 bottom-0 h-1 bg-primary-100 rounded-t-[2.5px]" />
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center justify-start gap-4 text-sm font-medium relative h-full overflow-x-scroll scrollbar-hidden max-lg:gap-4 max-lg:justify-start max-lg:w-full max-lg:whitespace-nowrap">
                        {reasons?.map((reason) => (
                            <div
                                key={reason.id}
                                className="relative clickable h-full flex items-center"
                                onClick={() => {
                                    setSelectedReason(selectedReason?.id === reason.id ? undefined : reason);
                                }}
                            >
                                <span
                                    className={`py-2 px-4 transition duration-300 ease-in-out ${selectedReason?.id === reason.id ? 'text-black-100' : 'text-black-60'
                                        }`}
                                >
                                    {reason.title}
                                </span>
                                {selectedReason?.id === reason.id && (
                                    <div className="absolute left-0 right-0 bottom-0 h-1 bg-primary-100 rounded-t-[2.5px]" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Spin spinning={loading}>
                <ul
                    className={`flex flex-col border ${isPreview ? '' : 'border-t-0'} border-primary-20 ${isPreview ? 'rounded-lg' : 'rounded-b-lg'
                        } bg-white`}
                >
                    {notifications?.items?.map((item) => (
                        <li
                            key={item.id}
                            onClick={() => onRead(item)}
                            className={`flex items-center justify-between pr-3 pl-4 h-14 max-lg:h-12 bg-white hover:bg-[#F1F8FF80] transition duration-200 ease-in-out rounded-lg ${!item.isRead ? 'clickable' : ''
                                }`}
                        >
                            <div className="flex items-center gap-3 max-lg:gap-[10px]">
                                <div className={`w-[6px] h-[6px] rounded-full ${item.isRead ? '' : 'bg-negative-default'}`} />
                                {renderIcon(item)}
                                <span className="font-bold text-sm text-darkPurple-100">{item.title}</span>
                                <span className="font-medium text-xs text-darkPurple-100 max-lg:w-32 max-lg:whitespace-nowrap max-lg:overflow-hidden max-lg:text-ellipsis">
                                    {item.message}
                                </span>
                            </div>
                            <span className="font-medium text-sm max-lg:text-xs">
                                {moment.utc(item?.createdOn).locale('fa').local().format('jD jMMMM jYYYY HH:mm')}
                            </span>
                        </li>
                    ))}
                    {!loading && notifications?.items?.length === 0 && <Empty />}
                </ul>
            </Spin>

            {!isPreview && (
                <ConfigProvider locale={fa_IR}>
                    <AntPagination
                        current={page}
                        pageSize={pageSize}
                        total={notifications?.pagination?.totalCount}
                        onChange={handlePageChange}
                        className="mt-4 justify-center"
                        style={{ direction: 'ltr' }}
                        showSizeChanger={isPreview ? false : true}
                        onShowSizeChange={(current, size) => handlePageChange(current, size)}
                    />
                </ConfigProvider>
            )}
        </>
    );
};